import { Injectable } from '@angular/core';
import { AumFinancialNumberPipe, DateLocalePipe, GICSCodesUtil } from '@modeso/aumico-lib-core-fe';
import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe';
import { ISectionData } from '@modeso/aumico-lib-annualreport-fe';
import { I18NextService } from 'angular-i18next';
import { cloneDeep } from 'lodash-es';
import {
    IAnnualReport,
    IFinStatements,
    IPrintOptions,
    IStyleOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { DEFAULT_AR_NAVIGATION } from '../../../../../../aumico-lib-annualreport-fe/src/lib/constants/default-ar-navigation';
import { PlaceholderHelper } from '../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/placeholder.helper';
import { IPdfTronMapper } from '../../../interfaces/pdf-tron-mapper.interface';
import { AnnualReportVisitor } from '../print-page/visitors/AnnualReportsVisitor';

@Injectable({
    providedIn: 'root',
})
export class AopSnapshotService {
    constructor(
        private readonly dateLocalePipe: DateLocalePipe,
        private readonly finNumberPipe: AumFinancialNumberPipe,
        private readonly i18next: I18NextService,
    ) {}

    buildPlaceholdersForPrint(annualReport: IAnnualReport, placeholderMap: IDynamicKeyValue): IDynamicKeyValue {
        const basePlaceholders = PlaceholderHelper.getPlaceholderMap(
            annualReport,
            this.dateLocalePipe,
            this.finNumberPipe,
        );
        const mergedPlaceholders: IDynamicKeyValue = { ...basePlaceholders };

        Object.entries(placeholderMap ?? {}).forEach(([key, value]) => {
            if (value == null) {
                return;
            }

            if (typeof value === 'string' && basePlaceholders[key] != null) {
                const trimmed = value.trim();
                if (trimmed.length === 0 || trimmed.startsWith('@')) {
                    return;
                }
            }

            mergedPlaceholders[key] = value;
        });

        return mergedPlaceholders;
    }

    replaceSnapshotPlaceholders(
        html: string,
        annualReport: IAnnualReport,
        placeholderMap: IDynamicKeyValue,
    ): string {
        if (html.trim().length === 0) {
            return html;
        }

        const placeholdersForPrint = this.buildPlaceholdersForPrint(annualReport, placeholderMap);
        let updatedHtml = html.replace(
            /<span[^>]*data-mention="([^"]+)"[^>]*>.*?<\/span>/g,
            (match, mentionTagName) => {
                if (!mentionTagName) {
                    return match;
                }

                const replacement = placeholdersForPrint[mentionTagName];
                return replacement != null ? String(replacement) : match;
            },
        );
        const entries = Object.entries(placeholdersForPrint)
            .filter(([, value]) => typeof value === 'string')
            .sort(([a], [b]) => b.length - a.length);

        for (const [key, value] of entries) {
            const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const keyRegex = new RegExp(escapedKey, 'g');
            updatedHtml = updatedHtml.replace(keyRegex, value as string);
        }

        return updatedHtml;
    }

    getSnapshotData(params: {
        annualReport: IAnnualReport;
        finStatements: IFinStatements[];
        printOptions: IPrintOptions;
        organization?: GetOrganizationDto;
        pdfTronMapper: IPdfTronMapper;
        currentLanguage?: string;
        defaultLanguage: string;
    }): {
        aopSection: ISectionData | undefined;
        snapshotStyleOptions: IStyleOptions;
        snapshotNoPreviousData: boolean;
    } {
        const {
            annualReport,
            finStatements,
            printOptions,
            organization,
            pdfTronMapper,
            currentLanguage,
            defaultLanguage,
        } = params;

        const reportLocaleValue = annualReport.locale ?? currentLanguage ?? defaultLanguage;
        const reportLocale = reportLocaleValue.toLowerCase();
        const activeLocale = (currentLanguage ?? reportLocaleValue).toLowerCase();
        const userLocale = activeLocale;
        const complementaryReportsTitles = (annualReport.complementaryReports ?? []).map(
            (report) => report?.title ?? '',
        );

        const annualReportVisitor = new AnnualReportVisitor(
            reportLocale,
            reportLocale,
            undefined,
            printOptions.reportSectionsTitles,
            this.i18next,
        );
        const predifinedLabels = annualReportVisitor.predifinedLabels;

        const pdfTronInput = pdfTronMapper.mapAnnualReportToPdfTronData(
            annualReport,
            finStatements,
            printOptions,
            organization,
            annualReport,
            DEFAULT_AR_NAVIGATION,
            complementaryReportsTitles,
            GICSCodesUtil.getAllGICSCodesAndSubindustryNames(),
            false,
            predifinedLabels,
            userLocale,
            activeLocale,
            reportLocale,
            null,
            undefined,
        );

        return {
            aopSection: pdfTronInput.aop,
            snapshotStyleOptions: cloneDeep<IStyleOptions>(printOptions.styleOptions),
            snapshotNoPreviousData: annualReport.generalInformation?.noPreviousData ?? false,
        };
    }
}
